<template>
  <div class="contents">
    <div class="layout view-log-analysis">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <h1 class="page-title">조회수 분석</h1>
      <p class="page-description">컨텐츠별 조회수 로그를 확인하고 분석할 수 있습니다.</p>
    </div>

    <!-- 필터 섹션 -->
    <FilterSection @reset="resetFilters">
      <template #filters>
        <div class="filter-item">
          <label for="contentType">컨텐츠 타입</label>
          <select 
            id="contentType" 
            v-model="selectedContentType" 
            @change="fetchViewLogs"
            class="form-select"
          >
            <option 
              v-for="option in contentTypeOptions" 
              :key="option.value" 
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </template>
      
      <template #actions>
        <button @click="resetFilters" class="btn btn-secondary">필터 초기화</button>
        <button @click="handleExport" class="btn btn-primary">데이터 내보내기</button>
      </template>
    </FilterSection>

    <!-- 통계 카드 섹션 -->
    <div class="stats-cards">
      <StatCard 
        icon="👁️"
        title="총 조회수"
        :value="totalViews"
        :format-number="true"
      />
      <StatCard 
        icon="📊"
        title="고유 방문자"
        :value="uniqueVisitors"
        :format-number="true"
      />
      <StatCard 
        icon="🎯"
        title="선택된 타입"
        :value="selectedContentType || '전체'"
      />
    </div>

    <!-- 데이터 테이블 -->
    <DataTable
      title="조회 로그 목록"
      :columns="tableColumns"
      :data="viewLogs"
      :loading="isLoading"
      :row-key="getRowKey"
      loading-text="로딩 중..."
      empty-text="조회 데이터가 없습니다."
    >
      <!-- 컨텐츠 타입 컬럼 -->
      <template #cell-contentType="{ value }">
        <ContentTypeBadge :type="value" />
      </template>

      <!-- 컨텐츠 제목 컬럼 -->
      <template #cell-contentTitle="{ value }">
        <span class="content-title" :title="value">
          {{ truncateText(value, 20) || '-' }}
        </span>
      </template>

      <!-- 사용자 컬럼 -->
      <template #cell-userAccountId="{ value }">
        <span class="user-info" :class="{ 'guest-user': value === 'GUEST' }" :title="value">
          {{ truncateText(value, 10) }}
        </span>
      </template>

      <!-- 세션 ID 컬럼 -->
      <template #cell-sessionId="{ value }">
        <span class="session-id">{{ formatSessionId(value) }}</span>
      </template>

      <!-- 조회 시간 컬럼 -->
      <template #cell-viewedAt="{ value }">
        <span class="timestamp">{{ formatDateTime(value) }}</span>
      </template>
    </DataTable>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import StatCard from '@/components/ui/StatCard.vue'
import DataTable from '@/components/ui/DataTable.vue'
import FilterSection from '@/components/ui/FilterSection.vue'
import ContentTypeBadge from '@/components/ui/ContentTypeBadge.vue'
import { useViewLogs } from '@/composables/useViewLogs'
import { useDataExport } from '@/composables/useDataExport'
import { 
  formatDateTime, 
  formatSessionId, 
  getContentTypeLabel, 
  CONTENT_TYPE_OPTIONS 
} from '@/utils/formatters'

// Composables
const {
  viewLogs,
  selectedContentType,
  isLoading,
  totalViews,
  uniqueVisitors,
  fetchViewLogs,
  resetFilters
} = useViewLogs()

const { exportToCSV } = useDataExport()

// 컨텐츠 타입 옵션
const contentTypeOptions = CONTENT_TYPE_OPTIONS

// 테이블 컬럼 정의
const tableColumns = [
  { key: 'contentType', title: '컨텐츠 타입', width: '120px' },
  { key: 'contentTitle', title: '컨텐츠 제목', minWidth: '150px' },
  { key: 'contentId', title: 'ID', width: '80px' },
  { key: 'userAccountId', title: '사용자', width: '100px' },
  { key: 'sessionId', title: '세션 ID', width: '100px', cellClass: 'session-id' },
  { key: 'ipAddress', title: 'IP 주소', width: '120px' },
  { key: 'viewedAt', title: '조회 시간', width: '180px', cellClass: 'timestamp' }
]

// CSV 내보내기용 컬럼 (포맷터 포함)
const csvColumns = [
  { 
    key: 'contentType', 
    title: '컨텐츠 타입',
    formatter: (value) => getContentTypeLabel(value)
  },
  { key: 'contentTitle', title: '컨텐츠 제목' },
  { key: 'contentId', title: '컨텐츠 ID' },
  { key: 'userAccountId', title: '사용자' },
  { key: 'sessionId', title: '세션 ID' },
  { key: 'ipAddress', title: 'IP 주소' },
  { 
    key: 'viewedAt', 
    title: '조회 시간',
    formatter: (value) => formatDateTime(value)
  }
]

// 테이블 행 키 생성
const getRowKey = (item) => {
  return `${item.contentType}-${item.contentId}-${item.sessionId}-${item.viewedAt}`
}

// 텍스트 자르기 함수
const truncateText = (text, maxLength) => {
  if (!text) return text
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// 데이터 내보내기
const handleExport = () => {
  const filename = `view-logs-${new Date().toISOString().split('T')[0]}.csv`
  exportToCSV(viewLogs.value, csvColumns, filename)
}

// 초기 데이터 로드
onMounted(() => {
  fetchViewLogs()
})
</script>

<style scoped>
.view-log-analysis {
  padding: 0;
  width: 100%;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.page-description {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-item label {
  font-weight: 600;
  color: #333;
}

.form-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  min-width: 150px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.user-info {
  font-weight: 500;
  display: inline-block;
}

.guest-user {
  color: #666;
  font-style: italic;
}

.content-title {
  font-weight: 500;
  color: #333;
  display: inline-block;
}

.session-id {
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.timestamp {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style>